import { useMutation, useQuery } from '@apollo/client'
import React, { useContext, useMemo, useState } from 'react'

import {
  GetCustomerAddressesDocument,
  SetDropShipOnCartDocument,
  SetShippingAddressesOnCartDocument,
  ShippingAddressInput,
} from '../../../graphql/magento'
import useLogAndCaptureError from '../../../hooks/useLogAndCaptureError'
import { TokenContext } from '../../../layouts/context'
import { NonEmptyCartContext } from '../../../lib/cart'
import { cartAddressInputFromFieldValues } from '../../../lib/cart/address'
import { DropShipInput } from '../../../lib/cart/constants'
import useLegalAgreementsGuard from '../../../lib/cart/hooks/useLegalAgreementsGuard'
import usePatientOnboardingCompleteGuard from '../../../lib/cart/hooks/usePatientOnboardingCompleteGuard'
import useRegistrationGuard from '../../../lib/cart/hooks/useRegistrationGuard'
import useShippingRestrictions from '../../../lib/cart/hooks/useShippingRestrictions'
import {
  isPractitionerShippingDetails,
  updateDropShipIfNeeded,
  updateShippingAddressIfNeeded,
} from '../../../lib/cart/shipping'
import { ValidFormValues } from '../../../lib/cart/types'
import { usePageUrl } from '../../../lib/pages'
import { logAndCaptureException } from '../../../utils/errorTools'
import Spinner from '../../Spinner'
import ShippingDetails from './ShippingDetails'

const ShippingDetailsContainer: React.FC = () => {
  const { loadingRedirect, shouldRedirect } = useRegistrationGuard()
  usePatientOnboardingCompleteGuard()
  useLegalAgreementsGuard()
  const token = useContext(TokenContext)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<Error | undefined>(undefined)
  const { navigate } = usePageUrl()

  const customerCart = useContext(NonEmptyCartContext)
  const shippingRestrictions = useShippingRestrictions()

  const {
    data: customerAddressesData,
    loading: customerAddressesLoading,
    error: customerAddressesError,
  } = useQuery(GetCustomerAddressesDocument, {
    context: { token },
  })

  useLogAndCaptureError(customerAddressesError)

  const [setDropShip] = useMutation(SetDropShipOnCartDocument, {
    context: { token },
  })

  const [setShippingAddressesOnCart] = useMutation(SetShippingAddressesOnCartDocument, {
    context: { token },
  })

  // NOTE: disabled for now (see related comment below)
  // const [setBillingAddressOnCart] = useMutation(SetBillingAddressOnCartDocument, {
  //   context: { token },
  // })

  // const updateBillingAddress = async (address: NonNullable<BillingAddressInput['address']>) => {
  //   return setBillingAddressOnCart({
  //     variables: {
  //       cartId: customerCart.id,
  //       billingAddress: {
  //         address,
  //         same_as_shipping: true,
  //       },
  //     },
  //   })
  // }

  const updateShippingAddress = (address: NonNullable<ShippingAddressInput['address']>) => {
    return setShippingAddressesOnCart({
      variables: {
        cartId: customerCart.id,
        shippingAddress: [
          {
            address,
          },
        ],
      },
      update(cache) {
        if (address.save_in_address_book) {
          // evict customer addresses from cache after saving new address
          cache.evict({ id: 'ROOT_QUERY', fieldName: 'customer' })
          cache.gc()
        }
      },
    })
  }

  const updateDropShip = (dropShip: string) => {
    return setDropShip({
      variables: {
        cartId: customerCart.id,
        dropShip,
      },
    })
  }

  const onSubmit = (values: ValidFormValues.ShippingDetails): void => {
    if (customerAddressesLoading) {
      logAndCaptureException(new Error('Customer addresses not loaded before onSubmit'))
    }

    setError(undefined)
    setSubmitting(true)
    const { dropShip: dropShipValue, ...addressValues } = isPractitionerShippingDetails(values)
      ? values
      : { ...values, dropShip: DropShipInput.NO }

    const addressInput = cartAddressInputFromFieldValues(values)
    const customerAddresses = customerAddressesData?.customer?.addresses

    Promise.resolve()
      .then(
        updateShippingAddressIfNeeded({
          addressInput,
          addressValues,
          // NOTE: disabled until further discussion - should the billing address
          //   be set to the shipping address initially or the customer's default
          //   billing address? It seems this originated from Well World checkout.
          // afterUpdate: updateBillingWithShippingAddressIfNeeded({
          //   addressInput,
          //   addressValues,
          //   customerCart,
          //   updateBillingAddress,
          // }),
          customerAddresses,
          customerCart,
          updateShippingAddress,
        }),
      )
      .then(
        updateDropShipIfNeeded({
          customerCart,
          dropShipValue,
          updateDropShip,
        }),
      )
      .then(() => navigate('/checkout/delivery'))
      .catch((err: unknown) => {
        // log server and application errors and capture in Sentry
        logAndCaptureException(err)
        setError(err instanceof Error ? err : new Error(String(err)))
        setSubmitting(false)
      })
  }

  const defaultShippingAddress = useMemo(() => {
    const addresses = customerAddressesData?.customer?.addresses
    return addresses
      ? addresses.find(
          (address) => address?.default_shipping && shippingRestrictions.isAddressAllowed(address),
        )
      : undefined
  }, [customerAddressesData?.customer?.addresses, shippingRestrictions])

  const cartShippingAddress = useMemo(() => {
    const address = customerCart?.shipping_addresses?.[0]
    return shippingRestrictions.isCartAddressAllowed(address) ? address : undefined
  }, [customerCart?.shipping_addresses, shippingRestrictions])

  if (loadingRedirect || shouldRedirect) {
    return <Spinner loading />
  }

  return (
    <ShippingDetails
      addressRestrictions={shippingRestrictions}
      defaultShippingAddress={defaultShippingAddress}
      error={error}
      onSubmit={onSubmit}
      shippingAddress={cartShippingAddress}
      submitting={submitting}
    />
  )
}

export default ShippingDetailsContainer
